/**
 * Reading a 401 body (ZLAYT-1649).
 *
 * The backend's exception handler reshapes every error into
 * {"error": {"message", "code", ...}}. Before ZLAYT-1649 it dropped
 * simplejwt's "code" and "messages" and set code "error" on every 401, so
 * only the message text told an expired access token apart; after it, the
 * code and messages are kept. Older code paths may still send simplejwt's
 * raw body ({"detail", "code", "messages"}). Every shape is read here so the
 * HTTP layer does not depend on which backend is deployed.
 */

type Body = Record<string, any> | null | undefined;

const envelopeOf = (data: Body): Record<string, any> => {
  if (!data || typeof data !== 'object') return {};
  return data.error && typeof data.error === 'object' ? data.error : data;
};

const messageOf = (envelope: Record<string, any>): string =>
  String(envelope.message ?? envelope.detail ?? '');

// simplejwt's text for a refused token sent in the Authorization header.
const ACCESS_TOKEN_REFUSED = /token not valid for any token type/i;

/**
 * True when the request was refused because its access token is expired or
 * not valid, so renewing the access token and resending can succeed. A
 * refused refresh token never reaches here: the renewal goes through plain
 * axios, outside the interceptor.
 */
export function isAccessTokenRefused(data: Body): boolean {
  const envelope = envelopeOf(data);
  const messages = Array.isArray(envelope.messages) ? envelope.messages : [];
  if (messages.some((item: any) => item?.token_type === 'access')) return true;
  return ACCESS_TOKEN_REFUSED.test(messageOf(envelope));
}

/** True when the account was deactivated: sign out, do not renew. */
export function isInactiveAccount(data: Body): boolean {
  const envelope = envelopeOf(data);
  if (envelope.code === 'user_inactive') return true;
  return ['User is inactive', 'user_inactive'].includes(messageOf(envelope));
}
